import { motion } from "framer-motion";
import { MapPin, Phone, Clock } from "lucide-react";
import { FaWhatsapp, FaInstagram, FaTelegram } from "react-icons/fa";
import { useLanguage } from "../context/LanguageContext";

const ContactSection = () => {
  const { t } = useLanguage();
  const whatsappNumber = "+905488484828";
  const whatsappLink = `https://wa.me/${whatsappNumber.replace(/\+/g, "")}?text=${encodeURIComponent(t("floatingWhatsapp.defaultMessage"))}`;

  const contactInfo = [
    {
      icon: Phone,
      labelKey: "contact.phone",
      value: "+90 548 848 4828",
      href: `tel:${whatsappNumber}`
    },
    {
      icon: MapPin,
      labelKey: "contact.location",
      valueKey: "contact.locationValue",
      href: "https://maps.google.com/?q=Kyrenia,Cyprus"
    },
    {
      icon: Clock,
      labelKey: "contact.hours",
      valueKey: "contact.hoursValue",
      href: null
    }
  ];

  return (
    <section id="contact" className="py-24 bg-[#050505] relative" data-testid="contact-section">
      {/* Background accent */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#FF00FF]/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-xs font-mono tracking-[0.3em] text-[#D4AF37] uppercase mb-4 block">
            {t("contact.subtitle")}
          </span>
          <h2 className="font-['Syne'] text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase mb-6">
            <span className="text-white">{t("contact.title1")}</span>{" "}
            <span className="neon-text-gold text-[#D4AF37]">{t("contact.title2")}</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            {t("contact.description")}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Contact Card */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass p-8 md:p-12"
            data-testid="contact-card"
          >
            {/* Contact Info */}
            <div className="space-y-6 mb-10">
              {contactInfo.map((item, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/5 border border-white/10 flex items-center justify-center">
                    <item.icon size={20} className="text-[#FF00FF]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                      {t(item.labelKey)}
                    </p>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="text-lg text-white hover:text-[#FF00FF] transition-colors"
                        data-testid={`contact-${item.labelKey}`}
                      >
                        {item.value || t(item.valueKey)}
                      </a>
                    ) : (
                      <p className="text-lg text-white">{item.value || t(item.valueKey)}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* WhatsApp CTA */}
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-3 bg-[#25D366] text-white py-5 font-bold text-lg tracking-wider uppercase hover:scale-[1.02] transition-all duration-300 shadow-[0_0_40px_rgba(37,211,102,0.3)] btn-clip"
              data-testid="contact-whatsapp-btn"
            >
              <FaWhatsapp size={26} />
              <span>{t("contact.whatsappBtn")}</span>
            </a>

            {/* Short Disclaimer under CTA */}
            <p className="text-gray-600 text-xs text-center mt-4 leading-relaxed">
              {t("legal.shortDisclaimer")}
            </p>

            {/* Social Links */}
            <div className="flex justify-center gap-4 mt-6">
              <a
                href="https://instagram.com/firstladynightclub"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-[#E1306C] hover:border-[#E1306C]/50 transition-all"
                aria-label="Instagram"
                data-testid="social-instagram"
              >
                <FaInstagram size={22} />
              </a>
              <a
                href="#"
                className="w-12 h-12 bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-[#0088cc] hover:border-[#0088cc]/50 transition-all"
                aria-label="Telegram"
                data-testid="social-telegram"
              >
                <FaTelegram size={22} />
              </a>
            </div>
          </motion.div>

          {/* Visual Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            {/* Big WhatsApp visual */}
            <div className="relative">
              <div className="absolute inset-0 bg-[#25D366]/10 rounded-full blur-[80px]" />
              <div className="relative bg-[#0F0F0F] border border-white/10 p-8 md:p-12 text-center">
                <div className="w-24 h-24 md:w-32 md:h-32 mx-auto mb-6 bg-[#25D366] rounded-full flex items-center justify-center float pulse-whatsapp">
                  <FaWhatsapp size={60} className="text-white" />
                </div>
                <h3 className="font-['Syne'] text-2xl md:text-3xl font-bold uppercase mb-4 text-white">
                  {t("contact.supportTitle")}
                </h3>
                <p className="text-gray-400 mb-6">
                  {t("contact.supportDesc")}
                </p>
                <div className="font-['Bebas_Neue'] text-4xl md:text-5xl text-[#25D366] tracking-wider">
                  +90 548 848 4828
                </div>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -top-4 -right-4 w-20 h-20 border border-[#FF00FF]/30" />
            <div className="absolute -bottom-4 -left-4 w-20 h-20 border border-[#00FFFF]/30" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
